/**
 * Rows that scroll sideways and never wrap (slice 11f; ui-style-guide "The roster screen"): the
 * Team tab's eleven thumbnails and its position chips. CSS does the scrolling (`.sideways`); this
 * only marks a row `data-more="true"` while more of it is off the trailing edge, which the CSS
 * draws as a fade, and keeps the mark right as the row scrolls or changes size.
 */

/** True while some of the row is past its trailing edge (a pixel of slack for sub-pixel widths). */
export function hasMore(row: HTMLElement): boolean {
  return row.scrollLeft + row.clientWidth < row.scrollWidth - 1;
}

function mark(row: HTMLElement): void {
  row.dataset.more = String(hasMore(row));
}

/** One observer for every row: a row's mark changes when the screen is resized or shown. */
export function sidewaysObserver(): ResizeObserver | null {
  return typeof ResizeObserver === 'function'
    ? new ResizeObserver((entries) => {
        for (const e of entries) mark(e.target as HTMLElement);
      })
    : null;
}

/** Mark the row now and on every scroll; `resize` re-marks it when its size changes. */
export function trackSideways(row: HTMLElement, resize: ResizeObserver | null): void {
  mark(row);
  row.addEventListener('scroll', () => mark(row), { passive: true });
  resize?.observe(row);
}

/**
 * Scroll the row sideways, and only sideways, until `child` is clear of the trailing fade
 * (`scroll-padding-inline` on the row). The page and the body never move.
 */
export function revealInline(row: HTMLElement, child: HTMLElement): void {
  const pad = parseFloat(getComputedStyle(row).scrollPaddingInlineEnd) || 0;
  const r = row.getBoundingClientRect();
  const c = child.getBoundingClientRect();
  if (c.left < r.left) row.scrollLeft -= r.left - c.left;
  else if (c.right > r.right - pad) row.scrollLeft += c.right - (r.right - pad);
}
